/**
 * @file     redactListingDetail.ts
 * @purpose  Server-side redaction of a full MLS ListingDetail down to the
 *           anonymous public record. IDX listing data is legally displayable,
 *           but exact rooftop coords, agent identity, every granular spec, and
 *           upstream CDN media URLs must not be handed to an unauthenticated/
 *           un-gated caller in the RSC payload or an API body. This keeps the
 *           public narrative + SEO + BCRES/DDF attribution set and nulls the
 *           private scrape-value fields.
 *
 *           Kept: full public remarks, address, price, beds/baths, type, sqft,
 *           year, city, status, proxied media, brokerage (officeName/office —
 *           mandatory IDX attribution), realtor.ca deep link + feedProvider
 *           (DDF obligation), coarsened coords, the price-change direction,
 *           and the public IDX facts (lot size, storeys, parking, features,
 *           owner 2026-10-09).
 *           Stripped: agent identity, exact coords, price history, taxes,
 *           strata fee, every VOW-only field (redactVowOnlyFields, which
 *           leaves a names-only presence list for teaser rows), upstream
 *           CloudFront media URLs.
 */

import type { ListingDetail } from './mlsPropertyService';
import { redactVowOnlyFields } from './vowOnlyFields';

/** Proxy URL — the only media host the client is ever allowed to see. Mirrors
 *  resolver.mlsMediaProxyUrl; upstream CloudFront URLs never ship to anon. */
function mediaProxyUrl(mediaKey: string): string {
  return `/api/mls/media/${encodeURIComponent(mediaKey)}`;
}

/** Round to ~street-block precision (3 decimals ≈ 110m) so the neighbourhood
 *  map still places the listing, but the exact rooftop point is not shipped. */
export function coarsenCoord(v: number | null): number | null {
  if (v == null) return null;
  return Math.round(v * 1000) / 1000;
}

/** Coarsen the coordinates on any card-shaped record (same ~110m rounding as
 *  the anonymous detail redaction). Used by public CDN-cached collection
 *  routes that must never ship exact rooftop points. */
export function coarsenCardCoordinates<T extends { latitude: number | null; longitude: number | null }>(card: T): T {
  return {
    ...card,
    latitude: coarsenCoord(card.latitude),
    longitude: coarsenCoord(card.longitude),
  };
}

/**
 * Redact a full ListingDetail to the anonymous public record. Spread-then-null
 * so new ListingDetail fields default to being CARRIED THROUGH — that is
 * deliberately the unsafe default, so this list must be revisited when the type
 * grows. The companion test (redactListingDetail.test.ts) asserts the sensitive
 * fields are gone and pins the kept public set.
 */
export function redactListingDetail(d: ListingDetail): ListingDetail {
  const vowSafe = redactVowOnlyFields(d);
  return {
    ...vowSafe,

    // PublicRemarks is the listing's public narrative and must remain complete
    // for anonymous visitors and search crawlers. The private scrape-value
    // fields below remain redacted.
    publicRemarks: d.publicRemarks,

    // Individual agent identity — stripped. Brokerage (office/officeName) is
    // KEPT: BCRES requires brokerage attribution on every display surface.
    agent: null,
    listAgentKey: null,
    listOfficeKey: null,

    // Exact rooftop location — coarsen.
    latitude: coarsenCoord(d.latitude),
    longitude: coarsenCoord(d.longitude),

    // Media: proxied only. Upstream CloudFront URLs never ship to anon.
    primaryMediaUrl: d.primaryMediaKey ? mediaProxyUrl(d.primaryMediaKey) : null,
    media: d.media.map((m) => ({
      mediaKey: m.mediaKey,
      mediaUrl: mediaProxyUrl(m.mediaKey),
      mediaOrder: m.mediaOrder,
    })),

    // Public IDX facts (size, rooms count, parking, features) ship to
    // everyone (owner, 2026-10-09: non-VOW fields may show). Kept for
    // signed-in visitors only: taxes and the strata fee, which the sign-in
    // prompt names. Every VOW-only field (vowOnlyFields.ts, cross-checked
    // against mls_field_rules) is already null via redactVowOnlyFields,
    // including the original list price: only its direction (priceTrend)
    // ships (owner, 2026-10-09).
    associationFee: null,
    taxAnnualAmount: null,
    taxYear: null,
  };
}
